import { Box, Text, Flex, Button, Spinner, Center } from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '../../ui';
import EMSDetailModal from '../../modals/EMSDetailModal';
import { useGetEms, type EMSPart } from '../../hooks/useGetEms';
import type { OrderEmsPush } from '../../hooks/useGetOrderDetail';

/**
 * EMS export history (ENG-2148): 4a diff-first ledger.
 *
 * Numbered exports newest-first, all collapsed until clicked. When the ledger
 * spans multiple days, date stubs split it into day groups and the rows
 * indent beneath them as sub-tabs. Expanding a row shows a change table vs
 * the last earlier export that was actually submitted (`previousSubmitted` —
 * skips unsent drafts). Open EMS uses EMSDetailModal.
 */

// 4a inspected values (Claude EMS Export History, option 4a).
// Yaxle `gray.*` is a different scale and must not be substituted here.
const C = {
    barBg: '#F7FAFC',
    barBorder: '#EDF2F7',
    date: '#2D3748',
    sub: '#718096',
    submittedFg: '#2F855A',
    pendingFg: '#B7791F',
    sparkSubmitted: '#68D391',
    sparkPending: '#F6AD55',
    rowHover: '#F7FAFC',
    rowBorder: '#EDF2F7',
    chevronCollapsed: '#CBD5E0',
    chevronExpanded: '#A0AEC0',
    name: '#1A202C',
    lines: '#2D3748',
    pillSubmittedBg: '#C6F6D5',
    pillSubmittedFg: '#22543D',
    pillPendingBg: '#FEEBC8',
    pillPendingFg: '#7B341E',
    added: '#2F855A',
    changed: '#553C9A',
    removed: '#C53030',
    neutral: '#A0AEC0',
    openEms: '#A0AEC0',
    openEmsHover: '#3182CE',
    openEmsHoverBg: '#EBF8FF',
    time: '#A0AEC0',
    headerMuted: '#718096',
    headerStrong: '#2D3748',
    tableBorder: '#E2E8F0',
    tableHeaderBg: '#F7FAFC',
    tableHeaderFg: '#718096',
    rowFg: '#2D3748',
    chipAddedBg: '#C6F6D5',
    chipAddedFg: '#22543D',
    chipRemovedBg: '#FED7D7',
    chipRemovedFg: '#822727',
    chipChangedBg: '#E9D8FD',
    chipChangedFg: '#44337A',
    typeAmBg: '#C6F6D5',
    typeAmFg: '#22543D',
    typeOemBg: '#BEE3F8',
    typeOemFg: '#2A4365',
    typeRcyBg: '#EDF2F7',
    typeRcyFg: '#718096',
    oePart: '#718096',
    struck: '#A0AEC0',
    footer: '#A0AEC0',
} as const;

const ROW_GRID = '16px 206px 78px 1fr 30px 74px';
const TABLE_GRID = '64px 74px 1fr 148px 48px 92px';

interface EmsExportDayTrackProps {
    /** Newest first — same order the drawer receives from the order payload. */
    pushes: Array<OrderEmsPush>;
    /**
     * Ledger mode (Exports "By RO" view): any number of exports may be open
     * at once. Default (drawer) is an accordion — opening a row closes the
     * other, and clicking the open row closes it. Both modes start collapsed.
     */
    multiOpen?: boolean;
}

interface TrackPush extends OrderEmsPush {
    exportNumber: number;
}

type LineChange =
    | { kind: 'added'; line: EMSPart }
    | { kind: 'changed'; line: EMSPart; previous: EMSPart }
    | { kind: 'removed'; line: EMSPart };

type ChangeKind = LineChange['kind'];

function formatDayLabel(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

/** Date stub between day groups: Today / Yesterday / full date. */
function formatDayStub(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    if (date.toDateString() === now.toDateString()) return 'Today';
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return formatDayLabel(dateStr);
}

function formatClockTime(dateStr: string): string {
    return new Date(dateStr).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
}

function formatPrice(value: number): string {
    return `$${value.toFixed(2)}`;
}

function sameCalendarDay(a: string, b: string): boolean {
    return new Date(a).toDateString() === new Date(b).toDateString();
}

function lineChanged(prev: EMSPart, next: EMSPart): boolean {
    return (
        prev.partNumber !== next.partNumber ||
        prev.type !== next.type ||
        prev.listPrice !== next.listPrice ||
        prev.partName !== next.partName ||
        prev.quantity !== next.quantity
    );
}

/** Newest-first list: the first older push that was sent to marketplaces. */
function previousSubmitted(numbered: Array<TrackPush>, selected: TrackPush | null): TrackPush | null {
    if (!selected) return null;
    const index = numbered.findIndex((push) => push._id === selected._id);
    if (index < 0) return null;
    return numbered.slice(index + 1).find((push) => push.wasSubmitted) ?? null;
}

function typeBadgeStyle(partType: string): { label: string; bg: string; color: string } {
    const type = partType?.toUpperCase() ?? '';
    if (type.includes('OEM') || type.includes('OE')) {
        return { label: 'OEM', bg: C.typeOemBg, color: C.typeOemFg };
    }
    if (type.includes('AFTERMARKET') || type.includes('A/M') || type.includes('AM') || type.includes('CAPA')) {
        return { label: 'A/M', bg: C.typeAmBg, color: C.typeAmFg };
    }
    if (type.includes('RECYCLED') || type.includes('LKQ') || type.includes('RCY') || type.includes('USED')) {
        return { label: 'RCY', bg: C.typeRcyBg, color: C.typeRcyFg };
    }
    return { label: partType || '—', bg: C.typeRcyBg, color: C.typeRcyFg };
}

const TypeBadge = ({ partType, struck = false }: { partType: string; struck?: boolean }) => {
    const { label, bg, color } = typeBadgeStyle(partType);
    return (
        <Box
            as="span"
            fontSize="10px"
            fontWeight="semibold"
            px="6px"
            py="2px"
            borderRadius="4px"
            bg={bg}
            color={color}
            textDecoration={struck ? 'line-through' : undefined}
            whiteSpace="nowrap"
        >
            {label}
        </Box>
    );
};

const ChangeChip = ({ kind, size = 15 }: { kind: ChangeKind; size?: number }) => {
    const dim = `${size}px`;
    if (kind === 'added') {
        return (
            <Flex
                w={dim}
                h={dim}
                borderRadius="4px"
                bg={C.chipAddedBg}
                color={C.chipAddedFg}
                fontSize="11px"
                fontWeight="bold"
                align="center"
                justify="center"
                flexShrink={0}
            >
                +
            </Flex>
        );
    }
    if (kind === 'removed') {
        return (
            <Flex
                w={dim}
                h={dim}
                borderRadius="4px"
                bg={C.chipRemovedBg}
                color={C.chipRemovedFg}
                fontSize="11px"
                fontWeight="bold"
                align="center"
                justify="center"
                flexShrink={0}
            >
                −
            </Flex>
        );
    }
    return (
        <Flex
            w={dim}
            h={dim}
            borderRadius="4px"
            bg={C.chipChangedBg}
            color={C.chipChangedFg}
            align="center"
            justify="center"
            flexShrink={0}
        >
            <Icon name="swap" size="9px" strokeWidth={3} />
        </Flex>
    );
};

const StatusPill = ({ wasSubmitted }: { wasSubmitted: boolean }) => (
    <Box
        as="span"
        fontSize="10.5px"
        fontWeight="semibold"
        px="7px"
        py="2px"
        borderRadius="full"
        bg={wasSubmitted ? C.pillSubmittedBg : C.pillPendingBg}
        color={wasSubmitted ? C.pillSubmittedFg : C.pillPendingFg}
        whiteSpace="nowrap"
    >
        {wasSubmitted ? 'Submitted' : 'Not submitted'}
    </Box>
);

const DiffCounts = ({
    isBaseline,
    isReady,
    added,
    changed,
    removed,
}: {
    isBaseline: boolean;
    isReady: boolean;
    added: number;
    changed: number;
    removed: number;
}) => {
    if (!isReady || isBaseline) {
        return (
            <Text fontSize="13px" color={C.neutral}>
                —
            </Text>
        );
    }
    if (added === 0 && changed === 0 && removed === 0) {
        return (
            <Text fontSize="13px" color={C.neutral}>
                No change
            </Text>
        );
    }
    return (
        <Flex align="baseline" gap="12px" fontSize="13px">
            {added > 0 && (
                <Text color={C.added} fontWeight="semibold">
                    +{added} added
                </Text>
            )}
            {changed > 0 && (
                <Text color={C.changed} fontWeight="semibold">
                    {changed} changed
                </Text>
            )}
            {removed > 0 && (
                <Text color={C.removed} fontWeight="semibold">
                    {removed} removed
                </Text>
            )}
        </Flex>
    );
};

const Stacked = ({
    oldNode,
    newNode,
    align = 'start',
}: {
    oldNode: React.ReactNode;
    newNode: React.ReactNode;
    align?: 'start' | 'end';
}) => (
    <Flex direction="column" gap="3px" align={align === 'end' ? 'flex-end' : 'flex-start'}>
        <Box color={C.struck} textDecoration="line-through">
            {oldNode}
        </Box>
        <Box fontWeight="semibold">{newNode}</Box>
    </Flex>
);

/** Per-row EMS + diff data, provided via render prop so any number of rows can be expanded at once. */
interface RowDatum {
    selectedEms: ReturnType<typeof useGetEms>['data'];
    isSelectedLoading: boolean;
    previousSent: TrackPush | null;
    isPreviousSentLoading: boolean;
    diff: { changes: Array<LineChange>; unchanged: Array<EMSPart>; isBaseline: boolean };
    addedCount: number;
    changedCount: number;
    removedCount: number;
    totalParts: number;
    diffReady: boolean;
    tableRows: Array<{ change: LineChange | null; line: EMSPart }>;
    showAllLines: boolean;
    setShowAllLines: React.Dispatch<React.SetStateAction<boolean>>;
}

function RowData({
    push,
    numbered,
    isExpanded,
    children,
}: {
    push: TrackPush;
    numbered: Array<TrackPush>;
    isExpanded: boolean;
    children: (datum: RowDatum) => React.ReactElement;
}) {
    const previousSent = previousSubmitted(numbered, push);

    const { data: selectedEms, isLoading: isSelectedLoading } = useGetEms(push._id, isExpanded);
    const { data: previousSentEms, isLoading: isPreviousSentLoading } = useGetEms(
        previousSent?._id ?? '',
        isExpanded && !!previousSent,
    );

    const [showAllLines, setShowAllLines] = useState(false);

    const diff = useMemo(() => {
        const currentParts = selectedEms?.parts ?? [];
        if (!previousSent || !previousSentEms) {
            return { changes: [] as Array<LineChange>, unchanged: currentParts, isBaseline: true };
        }
        const prevByLine = new Map(previousSentEms.parts.map((part) => [part.lineNumber, part]));
        const curLineNumbers = new Set(currentParts.map((part) => part.lineNumber));

        const changes: Array<LineChange> = [];
        const unchanged: Array<EMSPart> = [];
        for (const line of currentParts) {
            const prevLine = prevByLine.get(line.lineNumber);
            if (!prevLine) {
                changes.push({ kind: 'added', line });
            } else if (lineChanged(prevLine, line)) {
                changes.push({ kind: 'changed', line, previous: prevLine });
            } else {
                unchanged.push(line);
            }
        }
        for (const prevLine of previousSentEms.parts) {
            if (!curLineNumbers.has(prevLine.lineNumber)) {
                changes.push({ kind: 'removed', line: prevLine });
            }
        }
        changes.sort((a, b) => a.line.lineNumber - b.line.lineNumber);
        return { changes, unchanged, isBaseline: false };
    }, [selectedEms?.parts, previousSentEms, previousSent]);

    const addedCount = diff.changes.filter((c) => c.kind === 'added').length;
    const changedCount = diff.changes.filter((c) => c.kind === 'changed').length;
    const removedCount = diff.changes.filter((c) => c.kind === 'removed').length;
    const totalParts = selectedEms?.parts.length ?? 0;
    const diffReady = !isSelectedLoading && (!previousSent || !isPreviousSentLoading);

    useEffect(() => {
        if (!diffReady) return;
        // Only one EMS, or a later export with no line changes: open the
        // full estimate table. A real changelog stays change-only.
        setShowAllLines(diff.isBaseline || diff.changes.length === 0);
    }, [push._id, previousSent?._id, diffReady, diff.isBaseline, diff.changes.length]);

    const tableRows: Array<{ change: LineChange | null; line: EMSPart }> = showAllLines
        ? (selectedEms?.parts ?? [])
              .map((line) => ({
                  change: diff.changes.find((c) => c.kind !== 'removed' && c.line.lineNumber === line.lineNumber) ?? null,
                  line,
              }))
              .concat(diff.changes.filter((c) => c.kind === 'removed').map((c) => ({ change: c, line: c.line })))
              .sort((a, b) => a.line.lineNumber - b.line.lineNumber)
        : diff.changes.map((change) => ({ change, line: change.line }));

    return children({
        selectedEms,
        isSelectedLoading,
        previousSent,
        isPreviousSentLoading,
        diff,
        addedCount,
        changedCount,
        removedCount,
        totalParts,
        diffReady,
        tableRows,
        showAllLines,
        setShowAllLines,
    });
}

const EmsExportDayTrack = ({ pushes, multiOpen = false }: EmsExportDayTrackProps) => {
    const numbered: Array<TrackPush> = useMemo(
        () => pushes.map((push, index) => ({ ...push, exportNumber: pushes.length - index })),
        [pushes],
    );

    const chronological = useMemo(() => [...numbered].reverse(), [numbered]);
    const spansDays =
        chronological.length > 1 &&
        !sameCalendarDay(chronological[0].timestamp, chronological[chronological.length - 1].timestamp);

    // Every export starts collapsed in both modes.
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

    const [modalEmsId, setModalEmsId] = useState<string | null>(null);

    const isRowExpanded = (id: string) => expandedIds.has(id);

    const toggleRow = (id: string) => {
        setExpandedIds((previous) => {
            // Drawer accordion: one open at a time, clicking the open row closes it.
            if (!multiOpen) return previous.has(id) ? new Set<string>() : new Set([id]);
            const next = new Set(previous);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    if (numbered.length === 0) {
        return (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
                No EMS exports yet
            </Text>
        );
    }

    const first = chronological[0];
    const last = chronological[chronological.length - 1];
    const headerLabel = spansDays
        ? `${formatDayLabel(first.timestamp)} – ${formatDayLabel(last.timestamp)}`
        : formatDayLabel(first.timestamp);
    const submittedCount = numbered.filter((push) => push.wasSubmitted).length;
    const pendingCount = numbered.length - submittedCount;

    const openEms = (emsId: string, event: React.SyntheticEvent) => {
        event.stopPropagation();
        setModalEmsId(emsId);
    };

    return (
        <Box>
            <Flex
                align="center"
                justify="space-between"
                px="18px"
                py="14px"
                bg={C.barBg}
                border="1px solid"
                borderColor={C.barBorder}
                borderRadius="10px"
                boxShadow="none"
                gap={4}
            >
                <Flex align="baseline" gap="14px" minW={0} flexWrap="wrap">
                    <Text fontSize="13px" fontWeight="semibold" color={C.date} whiteSpace="nowrap">
                        {headerLabel}
                    </Text>
                    <Text fontSize="12.5px" color={C.sub} whiteSpace="nowrap">
                        <Box as="span" color={C.submittedFg} fontWeight="semibold">
                            {submittedCount} submitted
                        </Box>
                        {' · '}
                        <Box as="span" color={C.pendingFg} fontWeight="semibold">
                            {pendingCount} pending
                        </Box>
                    </Text>
                </Flex>
                <Flex gap="3px" w="200px" flexShrink={0}>
                    {chronological.map((push) => (
                        <Box
                            key={push._id}
                            flex="1"
                            h="6px"
                            borderRadius="full"
                            bg={push.wasSubmitted ? C.sparkSubmitted : C.sparkPending}
                        />
                    ))}
                </Flex>
            </Flex>

            <Box mt="10px" overflowX="auto">
                {numbered.map((push, index) => (
                    <RowData
                        key={push._id}
                        push={push}
                        numbered={numbered}
                        isExpanded={isRowExpanded(push._id)}
                    >
                        {({
                            selectedEms,
                            isSelectedLoading,
                            previousSent,
                            isPreviousSentLoading,
                            diff,
                            addedCount,
                            changedCount,
                            removedCount,
                            totalParts,
                            diffReady,
                            tableRows,
                            showAllLines,
                            setShowAllLines,
                        }) => {
                    const isExpanded = isRowExpanded(push._id);
                    const isLast = index === numbered.length - 1;
                    // Multi-day ledgers get a date stub above each day group;
                    // the exports render as sub-tabs indented beneath it.
                    const startsNewDay =
                        spansDays &&
                        (index === 0 || !sameCalendarDay(numbered[index - 1].timestamp, push.timestamp));
                    const dayCount = startsNewDay
                        ? numbered.filter((other) => sameCalendarDay(other.timestamp, push.timestamp)).length
                        : 0;
                    const isThisReady = isExpanded && diffReady;
                    const linesLabel =
                        isThisReady && selectedEms ? `${totalParts} line${totalParts === 1 ? '' : 's'}` : '';

                    return (
                        <Box>
                            {startsNewDay && (
                                <Flex align="center" gap="10px" px="10px" pt={index === 0 ? '4px' : '16px'} pb="4px">
                                    <Text
                                        fontSize="11px"
                                        fontWeight="semibold"
                                        color={C.sub}
                                        textTransform="uppercase"
                                        letterSpacing="0.05em"
                                        whiteSpace="nowrap"
                                    >
                                        {formatDayStub(push.timestamp)}
                                    </Text>
                                    <Box flex="1" h="1px" bg={C.rowBorder} />
                                    <Text
                                        fontSize="11px"
                                        color={C.neutral}
                                        whiteSpace="nowrap"
                                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                                    >
                                        {dayCount} export{dayCount === 1 ? '' : 's'}
                                    </Text>
                                </Flex>
                            )}
                            <Box
                                display="grid"
                                gridTemplateColumns={ROW_GRID}
                                columnGap="14px"
                                alignItems="center"
                                pl={spansDays ? '24px' : '10px'}
                                pr="10px"
                                py="13px"
                                borderBottom={isLast && !isExpanded ? 'none' : '1px solid'}
                                borderColor={C.rowBorder}
                                borderRadius="6px"
                                cursor="pointer"
                                role="button"
                                tabIndex={0}
                                aria-expanded={isExpanded}
                                onClick={() => toggleRow(push._id)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        toggleRow(push._id);
                                    }
                                }}
                                _hover={{ bg: C.rowHover }}
                            >
                                <Icon
                                    name="chevronRight"
                                    size="16px"
                                    color={isExpanded ? C.chevronExpanded : C.chevronCollapsed}
                                    transform={isExpanded ? 'rotate(90deg)' : undefined}
                                    transition="transform 0.15s ease"
                                />
                                <Flex align="center" gap="10px" minW={0}>
                                    <Text
                                        fontSize="14px"
                                        fontWeight="semibold"
                                        color={C.name}
                                        whiteSpace="nowrap"
                                    >
                                        EMS Export {push.exportNumber}
                                    </Text>
                                    <StatusPill wasSubmitted={push.wasSubmitted} />
                                </Flex>
                                <Text
                                    fontSize="13px"
                                    color={C.lines}
                                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                                >
                                    {linesLabel}
                                </Text>
                                <DiffCounts
                                    isBaseline={isThisReady ? diff.isBaseline : true}
                                    isReady={isThisReady}
                                    added={addedCount}
                                    changed={changedCount}
                                    removed={removedCount}
                                />
                                <Box
                                    as="button"
                                    type="button"
                                    title="Open full EMS screen"
                                    aria-label="Open full EMS screen"
                                    justifySelf="center"
                                    display="inline-flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    color={C.openEms}
                                    bg="transparent"
                                    border="none"
                                    p="4px"
                                    borderRadius="6px"
                                    cursor="pointer"
                                    onClick={(event: React.MouseEvent) => openEms(push._id, event)}
                                    onKeyDown={(event: React.KeyboardEvent) => event.stopPropagation()}
                                    _hover={{ color: C.openEmsHover, bg: C.openEmsHoverBg }}
                                >
                                    <Icon name="externalLink" size="15px" />
                                </Box>
                                <Text
                                    fontSize="12.5px"
                                    color={C.time}
                                    textAlign="right"
                                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                                >
                                    {formatClockTime(push.timestamp)}
                                </Text>
                            </Box>

                            {isExpanded && (
                                <Box
                                    pt="6px"
                                    pr="10px"
                                    pb="20px"
                                    pl={spansDays ? '44px' : '30px'}
                                    borderBottom={isLast ? 'none' : '1px solid'}
                                    borderColor={C.rowBorder}
                                >
                                    {isSelectedLoading || (previousSent && isPreviousSentLoading) ? (
                                        <Center py={6}>
                                            <Spinner size="sm" color="brand.500" />
                                        </Center>
                                    ) : (
                                        <>
                                            <Flex
                                                align="baseline"
                                                justify="space-between"
                                                px="4px"
                                                pb="8px"
                                                gap={3}
                                                flexWrap="wrap"
                                            >
                                                <Text fontSize="11.5px" color={C.headerMuted}>
                                                    {diff.isBaseline ? (
                                                        'No earlier submitted EMS to compare'
                                                    ) : (
                                                        <>
                                                            All changes from{' '}
                                                            <Box as="span" fontWeight="semibold" color={C.headerStrong}>
                                                                EMS Export {previousSent?.exportNumber}
                                                            </Box>
                                                        </>
                                                    )}
                                                </Text>
                                                {!diff.isBaseline && (
                                                    <Flex align="center" gap="14px" fontSize="11px" color={C.neutral}>
                                                        <Flex align="center" gap="5px">
                                                            <ChangeChip kind="added" size={14} />
                                                            added
                                                        </Flex>
                                                        <Flex align="center" gap="5px">
                                                            <ChangeChip kind="removed" size={14} />
                                                            removed
                                                        </Flex>
                                                        <Flex align="center" gap="5px">
                                                            <ChangeChip kind="changed" size={14} />
                                                            changed
                                                        </Flex>
                                                    </Flex>
                                                )}
                                            </Flex>

                                            {diff.isBaseline && !showAllLines ? (
                                                <Text fontSize="12.5px" color={C.neutral} px="4px" py={2}>
                                                    No earlier submitted EMS to compare against.
                                                </Text>
                                            ) : tableRows.length === 0 ? (
                                                <Text fontSize="12.5px" color={C.neutral} px="4px" py={2}>
                                                    No line changes since the last sent export.
                                                </Text>
                                            ) : (
                                                <Box
                                                    border="1px solid"
                                                    borderColor={C.tableBorder}
                                                    borderRadius="10px"
                                                    overflow="hidden"
                                                    overflowX="auto"
                                                    boxShadow="none"
                                                >
                                                    <Box
                                                        display="grid"
                                                        gridTemplateColumns={TABLE_GRID}
                                                        columnGap="12px"
                                                        px="14px"
                                                        py="8px"
                                                        bg={C.tableHeaderBg}
                                                        borderBottom="1px solid"
                                                        borderColor={C.tableBorder}
                                                        fontSize="10px"
                                                        fontWeight="semibold"
                                                        letterSpacing="0.06em"
                                                        textTransform="uppercase"
                                                        color={C.tableHeaderFg}
                                                    >
                                                        <Text as="span">Line</Text>
                                                        <Text as="span">Type</Text>
                                                        <Text as="span">Description</Text>
                                                        <Text as="span">OE Part #</Text>
                                                        <Text as="span" textAlign="right">
                                                            Qty
                                                        </Text>
                                                        <Text as="span" textAlign="right">
                                                            Price
                                                        </Text>
                                                    </Box>
                                                    {tableRows.map(({ change, line }, rowIndex) => {
                                                        const isRemoved = change?.kind === 'removed';
                                                        const isChanged = change?.kind === 'changed';
                                                        const previous = isChanged ? change.previous : null;
                                                        const typeChanged = Boolean(previous && previous.type !== line.type);
                                                        const nameChanged = Boolean(previous && previous.partName !== line.partName);
                                                        const partChanged = Boolean(
                                                            previous && previous.partNumber !== line.partNumber,
                                                        );
                                                        const qtyChanged = Boolean(previous && previous.quantity !== line.quantity);
                                                        const priceChanged = Boolean(
                                                            previous && previous.listPrice !== line.listPrice,
                                                        );
                                                        const isLastRow = rowIndex === tableRows.length - 1;
                                                        const strike = isRemoved ? 'line-through' : undefined;

                                                        return (
                                                            <Box
                                                                key={`${change?.kind ?? 'same'}-${line.lineNumber}`}
                                                                display="grid"
                                                                gridTemplateColumns={TABLE_GRID}
                                                                columnGap="12px"
                                                                px="14px"
                                                                py="9px"
                                                                borderBottom={isRemoved || isLastRow ? 'none' : '1px solid'}
                                                                borderColor={C.rowBorder}
                                                                fontSize="12.5px"
                                                                color={isRemoved ? C.neutral : C.rowFg}
                                                                alignItems="center"
                                                            >
                                                                <Flex align="center" gap="7px">
                                                                    {change ? <ChangeChip kind={change.kind} /> : <Box w="15px" />}
                                                                    <Text
                                                                        as="span"
                                                                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                                                                        textDecoration={strike}
                                                                    >
                                                                        {line.lineNumber}
                                                                    </Text>
                                                                </Flex>
                                                                <Box>
                                                                    {isChanged && typeChanged && previous ? (
                                                                        <Flex direction="column" gap="3px" align="flex-start">
                                                                            <TypeBadge partType={previous.type} struck />
                                                                            <TypeBadge partType={line.type} />
                                                                        </Flex>
                                                                    ) : (
                                                                        <TypeBadge partType={line.type} />
                                                                    )}
                                                                </Box>
                                                                <Box textDecoration={strike}>
                                                                    {isChanged && nameChanged && previous ? (
                                                                        <Stacked oldNode={previous.partName} newNode={line.partName} />
                                                                    ) : (
                                                                        line.partName
                                                                    )}
                                                                </Box>
                                                                <Box
                                                                    fontFamily="mono"
                                                                    color={isRemoved ? undefined : C.oePart}
                                                                    textDecoration={strike}
                                                                >
                                                                    {isChanged && partChanged && previous ? (
                                                                        <Stacked
                                                                            oldNode={previous.partNumber}
                                                                            newNode={line.partNumber}
                                                                        />
                                                                    ) : (
                                                                        line.partNumber
                                                                    )}
                                                                </Box>
                                                                <Box textAlign="right" textDecoration={isChanged && qtyChanged ? undefined : strike}>
                                                                    {isChanged && qtyChanged && previous ? (
                                                                        <Stacked
                                                                            oldNode={previous.quantity}
                                                                            newNode={line.quantity}
                                                                            align="end"
                                                                        />
                                                                    ) : (
                                                                        line.quantity
                                                                    )}
                                                                </Box>
                                                                <Box
                                                                    textAlign="right"
                                                                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                                                                    textDecoration={isChanged && priceChanged ? undefined : strike}
                                                                >
                                                                    {isChanged && priceChanged && previous ? (
                                                                        <Stacked
                                                                            oldNode={formatPrice(previous.listPrice)}
                                                                            newNode={formatPrice(line.listPrice)}
                                                                            align="end"
                                                                        />
                                                                    ) : (
                                                                        formatPrice(line.listPrice)
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}

                                            <Flex align="center" justify="space-between" mt="8px" px="4px" gap={3}>
                                                <Text fontSize="11.5px" color={C.footer}>
                                                    {showAllLines
                                                        ? `${totalParts} lines on this export`
                                                        : diff.isBaseline
                                                          ? `${totalParts} lines on export`
                                                          : `${diff.unchanged.length} unchanged lines not shown`}
                                                </Text>
                                                {totalParts > 0 && !diff.isBaseline && diff.changes.length > 0 && (
                                                    <Button
                                                        variant="link"
                                                        size="xs"
                                                        color="brand.500"
                                                        fontWeight="semibold"
                                                        onClick={() => setShowAllLines((value) => !value)}
                                                    >
                                                        {showAllLines ? 'Show changes only' : `Show all ${totalParts}`}
                                                    </Button>
                                                )}
                                            </Flex>
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>
                    );
                        }}
                    </RowData>
                ))}
            </Box>

            <EMSDetailModal
                isOpen={!!modalEmsId}
                onClose={() => setModalEmsId(null)}
                emsId={modalEmsId ?? ''}
            />
        </Box>
    );
};

export default EmsExportDayTrack;
